import { Button, Spinner } from '@ibsys/kit';
import { FC, MouseEvent } from 'react';
import { PlayIcon, SquareStopIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PhrasePlaybackTarget, usePhrasePlayback } from '../model/usePhrasePlayback';

type Props = PhrasePlaybackTarget & {
  /**
   * Достижима ли кнопка обходом с клавиатуры (по умолчанию да). Список, где обход идёт
   * по полям строк, выключает это: между полями соседних строк кнопке места нет.
   */
  isTabbable?: boolean;
};

/**
 * Кнопка прослушивания найденной реплики: запускает запись диалога за секунду до реплики
 * и обрывает её на конце реплики.
 *
 * Различает три состояния: предложение слушать, получение записи (от нажатия и до первого
 * звука, включая ожидание записи, заказанной у источника) и звучание. Во время получения
 * кнопка остаётся нажимаемой — повторным нажатием ожидание прекращается.
 */
export const PhrasePlayButton: FC<Props> = ({ isTabbable = true, ...target }) => {
  const { t } = useTranslation('dialogs');
  const { isPlaying, isLoading, toggle } = usePhrasePlayback(target);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Клик по кнопке не открывает диалог строки
    event.stopPropagation();
    toggle();
  };

  const getLabel = () => {
    if (isLoading) return t('phraseSearch.cancelLoading');
    if (isPlaying) return t('phraseSearch.stop');
    return t('phraseSearch.play');
  };

  // Спиннер как иконка, а не `isLoading` кнопки: та на время загрузки перестаёт нажиматься,
  // а ожидание записи нужно прекращать повторным нажатием
  const getIcon = () => {
    if (isLoading) return <Spinner className="size-3.5" />;
    if (isPlaying) return <SquareStopIcon className="size-3.5" />;
    return <PlayIcon className="size-3.5" />;
  };

  const label = getLabel();

  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-6"
      aria-label={label}
      tooltip={label}
      tabIndex={isTabbable ? undefined : -1}
      icon={getIcon()}
      onClick={handleClick}
    />
  );
};
